import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Provider,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  FormGroupComponent,
  InputComponent,
  MbsPopupType,
  ModalService,
  ModalSettings,
  SelectComponent,
  SwitcherComponent,
  SwitcherDescription,
  TableCellDirective,
  TableGridComponent,
  TableHeader,
  TextEllipsisComponent
} from 'mbs-ui-kit';
import { destinationToVMValidator, destinationValidator } from 'mbs-ui-shared@constants';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { defer, noop, of } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { RestoreSourceData } from 'wizards/services/wizard-steps.service';

import { CommonModule } from '@angular/common';

import { ReactiveFormsModule } from '@angular/forms';

import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

export type DestinationListItem = { name?: string; id?: string; specific?: string };
export type DestinationTableData = { overwriteExisting?: boolean; specificList?: string[]; valid: boolean };
export type DestinationTableDataForm = { overwriteExisting: FormControl<boolean>; specificList: FormArray<FormControl<string>> };

const DestinationTableValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationTableComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-table',
  templateUrl: './destination-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DestinationTableValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    InputComponent,
    NgbTooltipModule,
    ReactiveFormsModule,
    SelectComponent,
    SwitcherComponent,
    SwitcherDescription,
    TableGridComponent,
    TextEllipsisComponent,
    TableCellDirective
  ]
})
export class DestinationTableComponent implements OnInit, OnChanges {
  private cdr = inject(ChangeDetectorRef);
  i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);

  @ViewChild('changeNameModal', { static: true, read: TemplateRef }) changeNameModal: TemplateRef<HTMLElement>;

  @Input() forceValidate: boolean;
  @Input() showState: boolean;
  @Input() loadingTableData: boolean;
  @Input() isVirtual: boolean;
  @Input() planType: PlanTypes;
  @Input() dataForItems: { data: RestoreSourceData; items: unknown[] };

  public readonly mbsPopupType = MbsPopupType;
  public readonly elementsSelector = {
    name: {
      isAllRequired: 'isAllRequired',
      isNotUniq: 'isNotUniq',
      needOverwrite: 'needOverwrite'
    }
  };
  public readonly specificValidatorInstance = this.specificValidator.bind(this);

  onChange: (value: unknown) => void;
  onTouched: () => void;

  public textsMap: { editBtnTooltip: string; editNameDescription: string; editNameLabel: string; editNameTitle: string } = {
    editBtnTooltip: this.i18nPipe.transform('wizards:editVMNameTitle'),
    editNameDescription: this.i18nPipe.transform('wizards:editVMNameDescription'),
    editNameLabel: this.i18nPipe.transform('wizards:editVMNameLabel'),
    editNameTitle: this.i18nPipe.transform('wizards:editVMNameTitle')
  };

  public mainForm: FormGroup<DestinationTableDataForm>;

  public allItems: (unknown & { name?: string; Name?: string })[] = [];
  public listItems: DestinationListItem[] = [];

  public allItemsNamesHash: { [key: string]: boolean } = {};
  public itemForSelectsData: { [key: string]: Array<DefaultSelectData & { isSelected?: boolean }> } = {};

  public changeNameForm = new FormGroup<{ restoreAsName: FormControl<string> }>({
    restoreAsName: new FormControl('', [Validators.required, this.pathValidator.bind(this)])
  });

  private innerValue: DestinationTableData = null;
  set value(value: DestinationTableData) {
    this.innerValue = value;
    this.notifyValueChange();
  }
  get value(): DestinationTableData {
    return this.innerValue;
  }

  get changeNameSettings(): ModalSettings {
    return {
      header: { title: this.textsMap.editNameTitle },
      footer: {
        okButton: {
          text: this.i18nPipe.transform('shared:buttons.save', { format: 'title' }),
          disabled$: this.changeNameForm.valueChanges.pipe(
            startWith(true),
            switchMap(() => defer(() => of(!this.changeNameForm.valid)))
          )
        },
        cancelButton: { text: this.i18nPipe.transform('shared:buttons.cancel', { format: 'title' }) }
      }
    };
  }

  get isProxmox(): boolean {
    return this.planType === PlanTypes.RestoreProxmox;
  }

  get isDatabase(): boolean {
    return this.planType === PlanTypes.RestoreDataBasePlan;
  }

  get headers(): TableHeader[] {
    return [
      { name: this.isDatabase ? 'Selected database' : 'Selected virtual machine', gridColSize: '4fr' },
      { name: 'Restore as', overflow: true, gridColSize: '3fr', class: 'w-100' }
    ];
  }

  ngOnInit(): void {
    this.mainForm = new FormGroup<DestinationTableDataForm>({
      overwriteExisting: new FormControl(false),
      specificList: new FormArray([], this.selectedSpecificValidator.bind(this))
    });

    this.mainForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (value) => (this.value = { ...value, valid: this.mainForm.valid })
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mainForm) {
      if (changes.forceValidate?.currentValue) this.forceUpdateSpecificList();
      if (changes.loadingTableData) this.mainForm.get('specificList').updateValueAndValidity();
    }

    if (changes.dataForItems?.currentValue?.data) queueMicrotask(() => this.updateTableData());

    if (changes.planType?.currentValue && this.isDatabase) {
      this.textsMap = {
        editBtnTooltip: this.i18nPipe.transform('wizards:editDBNameTitle'),
        editNameDescription: this.i18nPipe.transform('wizards:editDBNameDescription'),
        editNameLabel: this.i18nPipe.transform('wizards:editDBNameLabel'),
        editNameTitle: this.i18nPipe.transform('wizards:editDBNameTitle')
      };
    }
  }

  pathValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    if (this.isProxmox) {
      return destinationToVMValidator.test(control.value)
        ? { invalidPath: { message: this.i18nPipe.transform('wizards:errorPathForVM') } }
        : null;
    }
    return destinationValidator.test(control.value)
      ? { invalidPath: { message: this.i18nPipe.transform('wizards:errorPathForVM') } }
      : null;
  }

  selectedSpecificValidator(control: FormArray): ValidationErrors | null {
    if (this.loadingTableData) return { isAllRequired: true };

    if (this.isVirtual || !control?.value?.length) return null;

    if (control.controls.some((c) => c.errors && c.errors.isAllRequired)) return { isAllRequired: true };
    if (control.controls.some((c) => c.errors && c.errors.isNotUniq)) return { isNotUniq: true };

    return control.controls.some((c) => c.errors && c.errors.needOverwrite) ? { needOverwrite: true } : null;
  }

  specificValidator(control: AbstractControl): ValidationErrors | null {
    if (this.isVirtual) return null;
    if (!control.value) return { isAllRequired: true };

    const names = control.value?.split('--');
    const notUnique = this.mainForm.get('specificList').value.some((path) => {
      const pathSlices = path.split('--');
      return names[0] !== pathSlices[0] && names[1] === pathSlices[1];
    });

    return this.allItemsNamesHash[names[1]] && !this.mainForm.get('overwriteExisting').value
      ? { needOverwrite: true }
      : notUnique
        ? { isNotUniq: true }
        : null;
  }

  forceUpdateSpecificList(): void {
    (this.mainForm.get('specificList') as FormArray)?.controls?.forEach((control) => {
      control.markAsTouched();
      control.markAsDirty();
      control.updateValueAndValidity();
    });
    this.mainForm.get('specificList').markAsDirty();
  }

  updateForm(value: DestinationTableData): void {
    if (!value) return;

    this.mainForm.reset(value);

    const mySpecificList: FormArray<FormControl<string>> = this.mainForm.get('specificList') as FormArray;

    mySpecificList.clear();
    value.specificList?.forEach((str) => mySpecificList.push(new FormControl(str, this.specificValidatorInstance)));
  }

  updateTableData(): void {
    this.allItems = this.dataForItems?.items || [];
    this.listItems = [];
    this.itemForSelectsData = {};
    this.allItemsNamesHash = {};

    const mapExistSpecific: { [key: string]: string } = {};
    const uniqSpecific: string[] = [];
    const machineNames = [];

    const fieldName = this.planType === PlanTypes.RestoreProxmox ? 'displayName' : this.allItems?.[0]?.name ? 'name' : 'Name';

    this.allItems.forEach((el: unknown & { displayName?: string; name?: string; Name?: string }) => {
      this.allItemsNamesHash[el[fieldName]] = true;
      machineNames.push(el[fieldName]);
    });

    this.value?.specificList?.forEach((str) => {
      const strArray = str.split('--').filter((s) => !!s);

      mapExistSpecific[strArray[0]] = strArray[1];

      if (!this.allItemsNamesHash[strArray[1]]) uniqSpecific.push(strArray[1]);
    });

    this.dataForItems.data.folders.forEach((machine: string, idx: number) => {
      const id = idx + machine.replaceAll(' ', '-');
      const strArray = machine.split('\\').filter((s) => !!s);
      const lastIndex = strArray.length > 3 ? 2 : strArray.length - 1;
      let specific = mapExistSpecific[machine] || strArray.slice(lastIndex).join('\\');

      if (!mapExistSpecific[machine] && this.planType === PlanTypes.RestoreProxmox) {
        specific = specific.split(' ').slice(1).join(' ');

        if (specific.startsWith('(') && specific.endsWith(')')) {
          specific = specific.slice(1, specific.length - 1);
        }
      }

      if (!mapExistSpecific[machine] && specific) uniqSpecific.push(specific);

      this.listItems.push({ name: machine, id, specific: specific });
      this.itemForSelectsData[id] = machineNames.map((str: string) => ({
        value: `${machine}--${str}`,
        label: str
      }));
      uniqSpecific.forEach((spec) => this.itemForSelectsData[id].push({ value: `${machine}--${spec}`, label: spec }));
    });

    this.updateFormControls();
  }

  updateFormControls(): void {
    if (!this.mainForm) return;

    const mySpecificList: FormArray<FormControl<string>> = this.mainForm.get('specificList') as FormArray;

    mySpecificList.clear();

    this.listItems.forEach((m) => mySpecificList.push(new FormControl(`${m.name}--${m.specific}`, this.specificValidatorInstance)));
  }

  overwriteExistingChangeHandler(): void {
    if (!this.showState) return;

    this.forceUpdateSpecificList();
    this.cdr.detectChanges();
  }

  editItemName(item: DestinationListItem, idx: number): void {
    this.changeNameForm.get('restoreAsName').setValue(item.specific);
    this.modalService
      .open(this.changeNameSettings, this.changeNameModal)
      .then((confirm) => {
        if (!confirm) return;

        const newValue = this.changeNameForm.get('restoreAsName').value;

        if (newValue === item.specific) return;

        const newData = this.itemForSelectsData[item.id].filter((el) => this.allItemsNamesHash[el.label]);

        item.specific = newValue;
        this.itemForSelectsData[item.id] = [...newData, { value: `${item.name}--${newValue}`, label: newValue }];
        const formArr = this.mainForm.get('specificList') as FormArray;
        formArr.get(idx.toString()).reset(`${item.name}--${newValue}`);
      })
      .catch(noop);
  }

  specificChangeHandler(event: string, machine: DestinationListItem): void {
    const names = event.split('--');

    machine.specific = names[1];

    this.forceUpdateSpecificList();
  }

  notifyValueChange(): void {
    if (this.onChange) {
      this.onChange(this.value);
      this.cdr.markForCheck();
    }
  }

  writeValue(value: DestinationTableData): void {
    this.innerValue = value;
    this.updateForm(value);
  }

  registerOnChange(fn: (data: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
